import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ElementIdentifierCreateType,
  ElementIdentifier,
  ElementIdentifierMetaData,
  ElementIdentifierLocatorType,
  ElementIdentifierDetails,
  ElementIdentifierHostType,
} from '@testsigma/organism';
import { TestDevelopmentContext } from '@modules/test-development/contexts/test-development-context';
import { VersionContext } from '@modules/test-development/contexts/version.context';
import { TestCase } from '@modules/test-development/models/test-case.model';
import { detectIncognito } from 'detectincognitojs';
import StrollEventService from '@services/strollr-event.service';
import { StrollrEventType } from '@enums/strollr-event-type.enum';
import { UserService } from '@services/user.service';
import { SessionContext } from '@contexts/session-context';

interface EventCallbacks {
  [key: string]: Function;
}

enum Message {
  MESSAGE_START_RECORDING = 'ts_1',
  MESSAGE_STOP_RECORDING = 'ts_2',
  MESSAGE_GET_UI_IDENTIFIER = 'ts_11',
  INSTALLED_PING_MESSAGE = 'ts_14',
  INSTALLED_PONG_MESSAGE = 'ts_15',
  MESSAGE_STOP_SPY = 'ts_16',
  MESSAGE_ELEMENT_TYPE = 'ts_17',
  MESSAGE_TYPE_STOPPED_RECORDING = 'ts_22',
  POST_INSTALLED_PONG_MESSAGE = 'ts_50',
  MESSAGE_GET_TABS_REQUEST = 'ts_55',
  MESSAGE_GET_APP_TABS_RESPONSE = 'ts_56',
  MESSAGE_SWITCH_TAB = 'ts_57',
  RECORDER_READY = 'ts_58',
  LAUNCH_RECORDER_WIDGET = 'ts_0',
  LAUNCH_SCREENRECORDER_WIDGET = 'ts_copilot_video_recorder',
  SCREENRECORDER_WINDGET_CLOSED = 'ts_copilot_video_recorder_end',
}
interface UseBrowserRecorderProps {
  eventCallbacks?: EventCallbacks;
}

const TS_WEB = 'testsigma_web';
const TS_CHROME = 'testsigma_chrome';

const useBrowserRecorder = (eventCallbacks?: UseBrowserRecorderProps) => {
  const [isExtensionInstalled, setIsExtensionInstalled] =
    useState<boolean>(false);
  const [browserTabs, setBrowserTabs] = useState<any>([]);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isRecorderLaunching, setIsRecorderLaunching] =
    useState<boolean>(false);
  const [lastEvent, setLastEvent] = useState<Message>();
  const [isIncognitoMode, setIsIncognitoMode] = useState<boolean>(false);
  const [isStepsRecording, setIsStepsRecording] = useState<boolean>(false);
  const [isScreenRecording, setIsScreenRecording] = useState<boolean>(false);

  const testDevelopmentContext = useContext(TestDevelopmentContext);
  const versionContext = useContext(VersionContext);
  const version = testDevelopmentContext?.version || versionContext?.version;

  const recordingBannerCloseHiddenElement = useRef<HTMLSpanElement>(null);

  const { session } = useContext(SessionContext);

  // returns true of userAgent is a browser
  const isChrome = useMemo(() => {
    const isChrome = navigator.userAgent.toLowerCase().indexOf('chrome');
    const isEdge = navigator.userAgent.toLowerCase().indexOf('edge');
    const isFireFox = navigator.userAgent.toLowerCase().indexOf('firefox');
    return isChrome > -1 || isEdge > -1 || isFireFox > -1;
  }, [navigator]);

  const isEdge = navigator.userAgent.includes('Edg');
  const isFireFox = navigator.userAgent.includes('Firefox');

  const eventListenerWindow = () => {
    return window.top ? window.top : window;
  };

  // Check if TestSigma recorder extension is enabled
  const checkExtension = () => {
    eventListenerWindow().postMessage(
      {
        from: TS_WEB,
        type: Message.INSTALLED_PING_MESSAGE,
        content: 'Get installed status',
      },
      '*',
    );
  };

  const checkIncognito = () => {
    detectIncognito().then((result) => {
      setIsIncognitoMode(result.isPrivate);
    });
  };

  const sendRecorderStartedEventToStrollr = () => {
    StrollEventService.track(StrollrEventType.WEB_BROWSER_RECORDER_STARTED);
  };

  const sendRecorderStoppedEventToStrollr = () => {
    StrollEventService.track(StrollrEventType.WEB_BROWSER_RECORDER_STOPPED);
  };

  const closeRecordingBanner = () => {
    recordingBannerCloseHiddenElement?.current?.click();
  };

  // on stop recording calls callback function for onStopRecording
  const stopRecordingCallbackCaller = useCallback(() => {
    setIsStepsRecording(false);
    setIsScreenRecording(false);
    sendRecorderStoppedEventToStrollr();
    closeRecordingBanner();
    eventCallbacks?.[Message.MESSAGE_STOP_RECORDING]?.();
  }, [eventCallbacks?.[Message.MESSAGE_STOP_RECORDING]]);

  // ??????
  const stopSingleElemRecordingCallbackCaller = useCallback(
    (element?: ElementIdentifier) => {
      eventCallbacks?.[Message.MESSAGE_ELEMENT_TYPE]?.(element);
    },
    [eventCallbacks?.[Message.MESSAGE_ELEMENT_TYPE]],
  );

  const stopScreenRecordingCallbackCaller = useCallback(
    (screenRecordingBase64?: string) => {
      eventCallbacks?.[Message.SCREENRECORDER_WINDGET_CLOSED]?.(
        screenRecordingBase64,
      );
    },
    [eventCallbacks?.[Message.SCREENRECORDER_WINDGET_CLOSED]],
  );

  // returns true if present inside nested context(shadow root), otherwise false
  const hasShadowTypeParents = (parentList) => {
    return (
      parentList?.some(
        (parent) => parent?.hostType === ElementIdentifierHostType.SHADOW,
      ) || false
    );
  };

  // creates and returns a new element using recorded elememt
  const processingUiIdentifier = useCallback(
    ({ data: currentUiIdentifier, screenshot, locatorTree }) => {
      const isImageLocator =
        currentUiIdentifier.locatorType == ElementIdentifierLocatorType.IMAGE;

      currentUiIdentifier.createdType = ElementIdentifierCreateType.CHROME;
      if (currentUiIdentifier?.canvas)
        currentUiIdentifier.screenShotData = currentUiIdentifier.canvas;
      if (currentUiIdentifier?.attr)
        currentUiIdentifier.attrs = currentUiIdentifier.attr;
      const uiIdentifier = new ElementIdentifier().deserialize(
        currentUiIdentifier,
      );
      uiIdentifier.metadata = new ElementIdentifierMetaData().deserialize(
        currentUiIdentifier.metaData,
      );
      if (currentUiIdentifier.elementDetails)
        currentUiIdentifier.elementDetails.data =
          currentUiIdentifier.elementDetails?.attributes;
      if (currentUiIdentifier?.screenTitle) {
        uiIdentifier.screenNameObj.name = currentUiIdentifier.screenTitle;
        uiIdentifier.screenName = currentUiIdentifier.screenTitle;
      }

      if (
        currentUiIdentifier?.locatorType &&
        currentUiIdentifier.locatorType != undefined
      ) {
        uiIdentifier.locatorType = currentUiIdentifier.locatorType;
        uiIdentifier.definition = currentUiIdentifier.defenition;

        if (!uiIdentifier.definition && !isImageLocator) {
          if (uiIdentifier.locatorType == ElementIdentifierLocatorType.xpath) {
            uiIdentifier.definition = currentUiIdentifier.xPath;
          } else if (
            uiIdentifier.locatorType == ElementIdentifierLocatorType.csspath
          ) {
            uiIdentifier.definition = currentUiIdentifier.cssPath;
          } else if (
            uiIdentifier.locatorType == ElementIdentifierLocatorType.id_value
          ) {
            uiIdentifier.definition = currentUiIdentifier?.attrs?.id || '';
          } else if (
            uiIdentifier.locatorType == ElementIdentifierLocatorType.name
          ) {
            uiIdentifier.definition = currentUiIdentifier.nameAttr || '';
          } else if (
            uiIdentifier.locatorType == ElementIdentifierLocatorType.link_text
          ) {
            uiIdentifier.definition = currentUiIdentifier.linkText || '';
          }
        }
      } else if (currentUiIdentifier?.xPath) {
        uiIdentifier.locatorType = ElementIdentifierLocatorType.xpath;
        uiIdentifier.definition = currentUiIdentifier.xPath;
      }

      if (!uiIdentifier?.applicationVersionId && version?.id)
        uiIdentifier.applicationVersionId = version.id;
      currentUiIdentifier.elementDetails?.attributes?.forEach((item, index) => {
        currentUiIdentifier.elementDetails.attributes[index]['attribute'] =
          currentUiIdentifier.elementDetails.attributes[index]['name'];
      });
      uiIdentifier.elementDetails = new ElementIdentifierDetails().deserialize(
        currentUiIdentifier.elementDetails,
      );
      uiIdentifier.parents = [];
      if (
        currentUiIdentifier.elementContext &&
        currentUiIdentifier.elementContext.length > 0
      ) {
        currentUiIdentifier.hostElements = currentUiIdentifier.elementContext;
        currentUiIdentifier.elementContext.forEach((parent) => {
          uiIdentifier.parents.push(parent);
        });
      } else {
        uiIdentifier.parents = currentUiIdentifier?.parents;
      }
      if (uiIdentifier.parents && uiIdentifier.parents.length > 0) {
        uiIdentifier.isInsideHosts = true;
        if (hasShadowTypeParents(uiIdentifier.parents)) {
          uiIdentifier.locatorType = ElementIdentifierLocatorType.csspath;
          uiIdentifier.definition = currentUiIdentifier.cssPath.trim();
        } else if (
          !uiIdentifier.locatorType ||
          (!uiIdentifier.definition && !isImageLocator)
        ) {
          uiIdentifier.locatorType = ElementIdentifierLocatorType.xpath;
          uiIdentifier.definition = currentUiIdentifier.xPath?.trim();
        }
      }
      if (screenshot) {
        uiIdentifier.screenshotDataUrl = screenshot;
      }
      if (locatorTree) {
        uiIdentifier.recordedSource = locatorTree?.root
          ? JSON.stringify(locatorTree.root)
          : null;
      }

      uiIdentifier.elementImageDraft =
        currentUiIdentifier?.elementImageDraft || null;
      return uiIdentifier;
    },
    [version?.id],
  );

  // listens to events sent from recorder
  const messageListener = useCallback(
    (eventRes) => {
      if (eventRes?.data?.from === TS_CHROME) {
        setLastEvent(eventRes.data.type);
      }
      if (eventRes.data.type === Message.MESSAGE_GET_APP_TABS_RESPONSE) {
        setBrowserTabs(eventRes.data.data.tabs);
      } else if (eventRes?.data?.type === Message.MESSAGE_STOP_RECORDING) {
        setIsRecording(false);
        stopRecordingCallbackCaller();
      } else if (eventRes?.data?.type === Message.MESSAGE_ELEMENT_TYPE) {
        setIsRecording(false);
        const element = processingUiIdentifier(eventRes?.data?.ele);
        stopSingleElemRecordingCallbackCaller(element);
      } else if (
        eventRes?.data?.type === Message.INSTALLED_PONG_MESSAGE ||
        eventRes?.data?.type === Message.POST_INSTALLED_PONG_MESSAGE
      ) {
        setIsExtensionInstalled(true);
      } else if (eventRes?.data?.type === Message.RECORDER_READY) {
        setIsRecording(true);
        setIsRecorderLaunching(false);
      } else if (
        eventRes?.data?.type === Message.SCREENRECORDER_WINDGET_CLOSED
      ) {
        setIsRecording(false);
        setIsScreenRecording(false);
        stopScreenRecordingCallbackCaller(
          eventRes?.data?.screenRecordingBase64,
        );
      }
    },
    [
      setLastEvent,
      setBrowserTabs,
      setIsRecording,
      stopRecordingCallbackCaller,
      processingUiIdentifier,
      stopSingleElemRecordingCallbackCaller,
      setIsExtensionInstalled,
    ],
  );

  // adds recorder event listners
  const injectListener = useCallback(() => {
    window.addEventListener('message', messageListener);
  }, [messageListener]);

  // removes recorder event listners
  const removeListener = useCallback(() => {
    window.removeEventListener('message', messageListener);
  }, [messageListener]);

  // sends message to recorder to fetch all windows
  const fetchTabs = useCallback(() => {
    eventListenerWindow().postMessage(
      {
        from: TS_WEB,
        type: Message.MESSAGE_GET_TABS_REQUEST,
        content: '',
      },
      '*',
    );
  }, [isChrome]);

  // sends message to recorder to start Element Recorder
  const recordElement = useCallback(
    async (isMultipleElementCapture?: boolean, isIncognitoMode?: boolean) => {
      eventListenerWindow().postMessage({
        from: TS_WEB,
        type: Message.LAUNCH_RECORDER_WIDGET,
        content: 'Launch recorder widget',
      });
      setIsRecording(true);

      const authToken = await UserService.getRecorderToken();

      eventListenerWindow().postMessage(
        {
          from: TS_WEB,
          type: Message.MESSAGE_GET_UI_IDENTIFIER,
          content: 'Get ui identifier',
          version_id: version?.id,
          isCloudFlag: true,
          element_capture: isMultipleElementCapture,
          applicationType: version?.application?.applicationType,
          serverUrl: window.location.origin,
          isNewUI: true,
          isIncognitoMode,
          authToken: authToken?.token || '',
        },
        '*',
      );
    },
    [version?.id, setIsRecording],
  );

  const recordSreenForCopilot = async (url: string) => {
    setIsRecorderLaunching(true);
    setIsScreenRecording(true);
    // const authToken = await UserService.getRecorderToken();
    setLastEvent(Message.LAUNCH_SCREENRECORDER_WIDGET);
    eventListenerWindow().postMessage(
      {
        from: TS_WEB,
        type: Message.LAUNCH_SCREENRECORDER_WIDGET,
        content: 'Start Video Recording',
        baseUrl: url,
      },
      '*',
    );
  };

  // sends message to recorder to start Test Step Recorder
  const recordSteps = useCallback(
    async (recorderTestCase: TestCase, isIncognitoMode?: boolean) => {
      eventListenerWindow().postMessage(
        {
          from: TS_WEB,
          type: Message.LAUNCH_RECORDER_WIDGET,
          content: 'Launch recorder widget',
        },
        '*',
      );

      setIsRecorderLaunching(true);
      setIsStepsRecording(true);
      const authToken = await UserService.getRecorderToken();

      setLastEvent(Message.MESSAGE_START_RECORDING);
      eventListenerWindow().postMessage(
        {
          from: TS_WEB,
          type: Message.MESSAGE_START_RECORDING,
          content: 'Start Recording',
          baseUrl: recorderTestCase.startUrl,
          test_case_id: recorderTestCase.id,
          version_id: recorderTestCase.applicationVersionId | version?.id,
          project_id: version.application.projectId,
          applicationType: version.application.applicationType,
          isCloudFlag: true,
          serverUrl: window.location.origin,
          applicationId: version?.application?.id,
          isNewUI: true,
          isIncognitoMode,
          authToken: authToken?.token || '',
          tenantFeatures: session.features,
          tenantPreference: session.tenantPreference,
        },
        '*',
      );
    },
    [version, setIsRecording],
  );

  // sends message to recorder to stop any Recorder
  const stopRecording = useCallback(
    (instantStop?: boolean) => {
      if (instantStop) {
        setIsRecording(false);
      }
      eventListenerWindow().postMessage(
        {
          from: TS_WEB,
          type: Message.MESSAGE_STOP_SPY,
          content: 'Stop Spying',
        },
        '*',
      );
    },
    [setIsRecording],
  );

  useEffect(() => {
    if (isStepsRecording && isRecording) {
      sendRecorderStartedEventToStrollr();
    }
  }, [isStepsRecording, isRecording]);

  // on load set event listners + fetch all windows + check if recorder extension installed
  useEffect(() => {
    injectListener();
    fetchTabs();
    checkExtension();
    checkIncognito();
  }, [isChrome]);

  // on unload remove event listners
  useEffect(() => {
    return () => {
      removeListener();
    };
  }, []);

  return {
    isChrome,
    isRecording,
    isRecorderLaunching,
    isExtensionInstalled,
    browserTabs,
    lastEvent,
    isIncognitoMode,
    recordElement,
    stopRecording,
    recordSteps,
    isEdge,
    isFireFox,
    recordingBannerCloseHiddenElement,
    recordSreenForCopilot,
    isScreenRecording,
  };
};

export const browserRecorderMessages = Message;
export default useBrowserRecorder;
